import React, { useEffect, useRef, useState } from "react";
import TimesheetKpiCard, { TIMESHEET_KPI_GRID_CLASS } from "./TimesheetKpiCard";
import useCareLabels from "hooks/useCareLabels";
import "./timesheet-detail-revamp.css";

const Pulse = ({ className = "" }) => (
  <div className={`animate-pulse rounded-md bg-[#E5E7EB] ${className}`} />
);

const TABLE_SK_ROW_HEIGHT = 60;
const TABLE_SK_HEADER_HEIGHT = 40;
const TABLE_SK_MIN_ROWS = 3;

const TABLE_SK_HEADER_LABELS_BASE = [
  "",
  "WORKER",
  null,
  "SHIFT DATE",
  "SCHEDULE",
  "ACTUAL",
  "STATUS",
  "TOTAL",
  "ACTION",
];

const TimesheetTableSkRow = ({ isHeader = false, participantColumnLabel }) => (
  <div
    className={`ts-table-sk-grid ${isHeader ? "ts-table-sk-header" : "ts-table-sk-row"}`}
  >
    {isHeader ? (
      TABLE_SK_HEADER_LABELS_BASE.map((label, index) => (
        <div key={index} className="ts-table-sk-cell">
          {(index === 2 ? participantColumnLabel : label) ? (
            <Pulse className="h-3 w-full max-w-[72px]" />
          ) : (
            <Pulse className="mx-auto h-4 w-4 rounded-[5px]" />
          )}
        </div>
      ))
    ) : (
      <>
        <div className="ts-table-sk-cell">
          <Pulse className="mx-auto h-4 w-4 rounded-[5px]" />
        </div>
        <div className="ts-table-sk-cell flex items-center gap-2">
          <Pulse className="h-[30px] w-[30px] shrink-0 rounded-full" />
          <Pulse className="h-2.5 w-[68px]" />
        </div>
        <div className="ts-table-sk-cell flex items-center gap-2">
          <Pulse className="h-6 w-6 shrink-0 rounded-full" />
          <Pulse className="h-2.5 w-[110px]" />
        </div>
        <div className="ts-table-sk-cell">
          <Pulse className="h-2.5 w-[52px]" />
        </div>
        <div className="ts-table-sk-cell">
          <Pulse className="h-2.5 w-[52px]" />
        </div>
        <div className="ts-table-sk-cell">
          <Pulse className="h-2.5 w-[52px]" />
        </div>
        <div className="ts-table-sk-cell">
          <Pulse className="h-5 w-[72px] rounded-full" />
        </div>
        <div className="ts-table-sk-cell">
          <Pulse className="h-2.5 w-8" />
        </div>
        <div className="ts-table-sk-cell flex justify-center gap-1.5">
          <Pulse className="h-8 w-8 rounded-full" />
          <Pulse className="h-8 w-8 rounded-full" />
        </div>
      </>
    )}
  </div>
);

const useTableSkeletonRowCount = () => {
  const bodyRef = useRef(null);
  const [rowCount, setRowCount] = useState(8);

  useEffect(() => {
    const node = bodyRef.current;
    if (!node) return;

    const syncRowCount = () => {
      const rowsHeight = node.clientHeight - TABLE_SK_HEADER_HEIGHT;
      const nextCount = Math.max(
        TABLE_SK_MIN_ROWS,
        Math.ceil(rowsHeight / TABLE_SK_ROW_HEIGHT)
      );
      setRowCount((prev) => (prev === nextCount ? prev : nextCount));
    };

    syncRowCount();

    if (typeof ResizeObserver === "undefined") return undefined;

    const observer = new ResizeObserver(syncRowCount);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return { bodyRef, rowCount };
};

const TimesheetTableSkScrollArea = ({ className = "" }) => {
  const { bodyRef, rowCount } = useTableSkeletonRowCount();
  const { participantLabelPlural } = useCareLabels();
  const participantColumnLabel = participantLabelPlural.toUpperCase();

  return (
    <div
      ref={bodyRef}
      className={`flex min-h-0 flex-1 flex-col overflow-x-auto overflow-y-hidden ${className}`}
    >
      <TimesheetTableSkRow isHeader participantColumnLabel={participantColumnLabel} />
      {Array.from({ length: rowCount }).map((_, i) => (
        <TimesheetTableSkRow key={i} />
      ))}
    </div>
  );
};

export const TimesheetKpiSkeleton = () => (
  <div className={TIMESHEET_KPI_GRID_CLASS}>
    {Array.from({ length: 6 }).map((_, i) => (
      <TimesheetKpiCard key={i} skeleton />
    ))}
  </div>
);

export const TimesheetFilterSkeleton = () => (
  <div className="flex flex-col gap-3 rounded-2xl border border-[#E5E7EB] bg-white p-3 sm:p-3.5 md:flex-row md:items-end md:gap-2.5">
    <div className="flex w-full flex-col gap-1 md:w-auto">
      <Pulse className="h-2.5 w-16" />
      <Pulse className="h-[34px] w-full rounded-[10px] md:w-[200px]" />
    </div>
    <div className="flex w-full flex-col gap-1 md:w-auto">
      <Pulse className="h-2.5 w-14" />
      <Pulse className="h-[34px] w-full rounded-[10px] md:w-[220px]" />
    </div>
    <div className="flex w-full flex-col gap-1 lg:hidden">
      <Pulse className="h-2.5 w-10" />
      <Pulse className="h-[34px] w-full rounded-[10px]" />
    </div>
    <div className="hidden min-w-0 flex-1 flex-col gap-1 lg:flex">
      <Pulse className="h-2.5 w-10" />
      <Pulse className="h-[34px] w-full max-w-[320px] rounded-[10px]" />
    </div>
  </div>
);

export const TimesheetWorkerListSkeleton = () => (
  <div className="space-y-2">
    {Array.from({ length: 6 }).map((_, i) => (
      <div
        key={i}
        className="flex items-center gap-2.5 rounded-xl border border-[#E5E7EB] p-2.5"
      >
        <Pulse className="h-9 w-9 shrink-0 rounded-full" />
        <div className="flex-1 space-y-1.5">
          <Pulse className="h-3.5 w-24" />
          <Pulse className="h-3 w-32" />
        </div>
        <Pulse className="h-5 w-5 rounded-full" />
      </div>
    ))}
  </div>
);

export const TimesheetWorkerSidebarSkeleton = () => (
  <div className="flex h-full flex-col overflow-hidden rounded-2xl border border-[#E5E7EB] bg-white">
    <div className="flex items-center justify-between border-b border-[#E5E7EB] px-4 py-3">
      <Pulse className="h-4 w-20" />
      <Pulse className="h-5 w-8 rounded-full" />
    </div>
    <div className="border-b border-[#E5E7EB] p-3">
      <Pulse className="h-[34px] w-full rounded-[10px]" />
    </div>
    <div className="p-3">
      <TimesheetWorkerListSkeleton />
    </div>
  </div>
);

/** Table rows only — fits inside the table card (bulk toolbar stays visible). */
export const TimesheetTableBodySkeleton = () => (
  <div className="flex h-full min-h-0 flex-col bg-white">
    <TimesheetTableSkScrollArea />
    <div className="shrink-0 border-t border-[#E5E7EB] px-4 py-2">
      <Pulse className="h-8 w-full max-w-xs" />
    </div>
  </div>
);

export const TimesheetTableSkeleton = () => (
  <div className="flex h-full flex-col overflow-hidden rounded-2xl border border-[#E5E7EB] bg-white">
    <div className="flex shrink-0 items-center justify-between border-b border-[#E5E7EB] px-4 py-2.5">
      <Pulse className="h-4 w-32" />
      <div className="flex gap-2">
        <Pulse className="h-8 w-24 rounded-lg" />
        <Pulse className="h-8 w-24 rounded-lg" />
      </div>
    </div>
    <TimesheetTableSkScrollArea />
    <div className="shrink-0 border-t border-[#E5E7EB] px-4 py-2">
      <Pulse className="h-8 w-full max-w-xs" />
    </div>
  </div>
);

const TimesheetPageSkeleton = () => (
  <div className="flex min-w-0 flex-col gap-3">
    <TimesheetKpiSkeleton />
    <TimesheetFilterSkeleton />
    <div
      className="timesheet-workspace flex flex-col gap-3 lg:flex-row lg:gap-4"
      style={{ "--timesheet-workspace-h": "560px" }}
    >
      <div className="timesheet-workspace-sidebar h-[min(360px,50vh)] w-full shrink-0 lg:w-[260px]">
        <TimesheetWorkerSidebarSkeleton />
      </div>
      <div className="timesheet-workspace-table min-h-[280px] h-[min(440px,52vh)] w-full min-w-0 lg:h-auto lg:min-h-0 lg:flex-1">
        <TimesheetTableSkeleton />
      </div>
    </div>
  </div>
);

export default TimesheetPageSkeleton;
